import { motion, AnimatePresence } from "framer-motion";
import { Download, ShieldAlert, X } from "lucide-react";
import { useLang } from "@/i18n";

const reveal = {
    initial: { opacity: 0, y: 26 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.15 },
};

export default function InstallGuide({ onDownload }) {
    const { t } = useLang();
    const c = t.install;

    return (
        <section id="install" className="grain relative scroll-mt-24 overflow-hidden bg-ink py-16 text-white sm:py-24">
            <div className="hero-grid absolute inset-0 opacity-40" aria-hidden="true" />
            <div className="relative mx-auto max-w-5xl px-4 sm:px-6">
                <motion.div {...reveal} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }} className="max-w-2xl">
                    <p className="text-xs font-bold uppercase tracking-[0.22em] text-gold">{c.eyebrow}</p>
                    <h2 className="font-display mt-2 text-3xl font-bold tracking-tight sm:text-4xl">{c.title}</h2>
                    <p className="mt-3 text-white/70">{c.sub}</p>
                </motion.div>

                <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {c.steps.map((s, i) => (
                        <motion.div
                            key={i}
                            {...reveal}
                            transition={{ duration: 0.6, delay: 0.07 * i, ease: [0.16, 1, 0.3, 1] }}
                            className="rounded-3xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm"
                            data-testid={`install-step-${i + 1}`}
                        >
                            <p className="font-display text-4xl font-bold text-gold/90">
                                {String(i + 1).padStart(2, "0")}
                            </p>
                            <p className="mt-3 text-sm leading-relaxed text-white/85">{s}</p>
                        </motion.div>
                    ))}
                </div>

                <motion.div
                    {...reveal}
                    transition={{ duration: 0.6, delay: 0.15 }}
                    className="mt-8 flex flex-col items-start gap-5 sm:flex-row sm:items-center"
                >
                    <button
                        type="button"
                        onClick={onDownload}
                        data-testid="install-section-download-button"
                        className="flex h-13 min-h-12 items-center gap-2.5 rounded-full bg-white px-7 font-bold text-ink transition-all hover:-translate-y-0.5 hover:bg-gold"
                    >
                        <Download className="h-5 w-5" />
                        {c.dlBtn}
                    </button>
                    <p className="flex items-start gap-2 max-w-md text-xs leading-relaxed text-white/60">
                        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                        {c.note}
                    </p>
                </motion.div>
            </div>
        </section>
    );
}

export function ApkModal({ open, onClose, onDownload }) {
    const { t } = useLang();
    const c = t.install;

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-end justify-center bg-ink/60 p-4 backdrop-blur-sm sm:items-center"
                    onClick={onClose}
                    data-testid="apk-installation-guide-modal"
                >
                    <motion.div
                        initial={{ y: 40, opacity: 0, scale: 0.98 }}
                        animate={{ y: 0, opacity: 1, scale: 1 }}
                        exit={{ y: 30, opacity: 0, scale: 0.98 }}
                        transition={{ type: "spring", stiffness: 260, damping: 26 }}
                        className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl sm:p-7"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between gap-3">
                            <h3 className="font-display text-xl font-bold text-ink">{t.modal.title}</h3>
                            <button
                                type="button"
                                onClick={onClose}
                                aria-label="Close"
                                data-testid="apk-modal-close-button"
                                className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition-colors hover:bg-slate-200"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <p className="mt-1.5 text-sm text-slate-600">{t.modal.sub}</p>

                        <ol className="mt-5 space-y-3.5">
                            {c.steps.map((s, i) => (
                                <li key={i} className="flex gap-3">
                                    <span className="font-display flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand text-xs font-bold text-white">
                                        {i + 1}
                                    </span>
                                    <span className="text-sm leading-relaxed text-slate-700">{s}</span>
                                </li>
                            ))}
                        </ol>

                        <div className="mt-5 rounded-2xl border border-gold/40 bg-amber-50 p-3.5 text-xs leading-relaxed text-amber-900">
                            {c.note}
                        </div>

                        <div className="mt-5 flex gap-3">
                            <button
                                type="button"
                                onClick={onClose}
                                data-testid="apk-modal-got-it-button"
                                className="h-12 flex-1 rounded-full bg-brand font-semibold text-white transition-colors hover:bg-brand-dark"
                            >
                                {t.modal.got}
                            </button>
                            <button
                                type="button"
                                onClick={onDownload}
                                data-testid="apk-modal-download-again-button"
                                className="h-12 flex-1 rounded-full border-2 border-brand font-semibold text-brand transition-colors hover:bg-brand-soft"
                            >
                                {t.modal.again}
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
